import React, { useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";

import DisplayHeading from "./display-heading";

export interface FieldInfoValue {
  label: string;
  description: string;
  sx?: SxProps<Theme>;
}

export interface FieldInfoProps {
  // Unique per field, e.g. "field-info-payment-status". Drives every data-testid below.
  testId: string;
  title: string;
  description: string;
  values: FieldInfoValue[];
  // This org's current value, if it matches one of `values` - shown in the hover tooltip so the
  // most common question ("what does *this* value mean?") is answered without opening the dialog.
  currentValue?: string;
  // Rendered at the bottom of the dialog, e.g. a link to further docs or a fallback-value caveat.
  extraNote?: React.ReactNode;
}

const firstSentence = (text: string): string => {
  const [sentence] = text.split(". ");
  return sentence.endsWith(".") ? sentence : `${sentence}.`;
};

const FieldInfo = ({
  testId,
  title,
  description,
  values,
  currentValue,
  extraNote,
}: FieldInfoProps) => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const current = values.find((value) => value.label === currentValue);
  // action.hover's ~4% overlay reads as barely-there noise against the dialog's own paper
  // background. Nudge paper itself lighter (dark mode) or darker (light mode) instead - a
  // shade that's visibly distinct but still close in hue, rather than jumping to the app's
  // much darker page-level background.default.
  const valueRowBg =
    theme.palette.mode === "dark"
      ? lighten(theme.palette.background.paper, 0.08)
      : darken(theme.palette.background.paper, 0.04);

  const tooltipContent = (
    <Box data-testid={`${testId}-tooltip`}>
      <Typography variant="subtitle2" component="p">
        {title}
      </Typography>
      <Typography variant="caption" component="p">
        {firstSentence(description)}
      </Typography>
      {current && (
        <Box mt={1}>
          <Typography variant="overline" component="p" sx={{ opacity: 0.75 }}>
            This org
          </Typography>
          <Chip
            size="small"
            label={current.label}
            sx={{ fontWeight: "bold", ...current.sx }}
          />
          <Typography variant="caption" component="p" mt={0.5}>
            {current.description}
          </Typography>
        </Box>
      )}
      <Typography
        component="button"
        variant="caption"
        onClick={() => setDialogOpen(true)}
        data-testid={`${testId}-find-out-more`}
        sx={{
          display: "block",
          mt: 1,
          border: 0,
          background: "none",
          padding: 0,
          font: "inherit",
          fontWeight: 600,
          color: (theme) => theme.palette.primary.light,
          cursor: "pointer",
        }}
      >
        Find out more…
      </Typography>
    </Box>
  );

  return (
    <>
      <Tooltip title={tooltipContent}>
        <IconButton
          size="small"
          onClick={() => setDialogOpen(true)}
          data-testid={`${testId}-icon`}
          aria-label={`About ${title}`}
        >
          <InfoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        data-testid={`${testId}-dialog`}
      >
        <Box sx={{ position: "relative", textAlign: "center", pt: 4, pb: 2 }}>
          <DisplayHeading>{title}</DisplayHeading>
          <IconButton
            onClick={() => setDialogOpen(false)}
            data-testid={`${testId}-dialog-close`}
            aria-label="Close"
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 0, pb: 3 }}>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {description}
          </Typography>
          <Typography variant="overline" color="text.secondary">
            Possible values
          </Typography>
          <Box
            display="grid"
            sx={{
              gridTemplateColumns: "auto 1fr",
              mt: 1.5,
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {values.map((value) => (
              <React.Fragment key={value.label}>
                <Box
                  data-testid={`${testId}-value-${value.label}`}
                  sx={{
                    bgcolor: valueRowBg,
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Chip
                    size="small"
                    label={value.label}
                    sx={{ fontWeight: "bold", ...value.sx }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    bgcolor: valueRowBg,
                    px: 1.5,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {value.description}
                </Typography>
              </React.Fragment>
            ))}
          </Box>
          {extraNote && (
            <Box
              data-testid={`${testId}-extra-note`}
              sx={{
                mt: 3,
                py: 1,
                pl: 1.5,
                pr: 1,
                borderLeft: 3,
                borderColor: "primary.main",
                borderRadius: "0 4px 4px 0",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {extraNote}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FieldInfo;
